import { useEffect, useState, useCallback } from "react";
import { BarChart3, RefreshCw, Users, GraduationCap } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useCorporate } from "./CorporateLayout";

export default function CorporateAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { corp } = useCorporate();
  const isSchoolGroup = corp?.kind === "school";

  const load = useCallback(() => {
    setLoading(true);
    api.get("/corporate/dashboard")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const locations = data?.locations || [];
  const totals = data?.totals || {};
  const chartData = locations.map((l) => ({
    name: l.company_name.length > 18 ? l.company_name.slice(0, 17) + "…" : l.company_name,
    students: l.students_on_premises || 0,
    visitors: l.visitors_inside || 0,
    todays_events: (l.type === "school" ? l.todays_student_events : l.todays_visitors) || 0,
  }));

  return (
    <main className="px-6 md:px-10 py-8" data-testid="corporate-analytics-page">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" /> District analytics
          </h1>
          <p className="text-sm text-slate-600">Compare activity across every location right now.</p>
        </div>
        <button onClick={load} data-testid="corporate-refresh-btn" title="Refresh" className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 transition">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <section className={`grid grid-cols-2 ${isSchoolGroup ? "md:grid-cols-3" : ""} gap-4 mb-6`} data-testid="analytics-totals">
        {isSchoolGroup && <TotalCard Icon={GraduationCap} label="Students on premises" value={totals.students_on_premises || 0} accent="emerald" />}
        <TotalCard Icon={Users} label="Visitors inside now" value={totals.visitors_inside || 0} accent="primary" />
        <TotalCard Icon={BarChart3} label="Events today" value={(totals.todays_student_events || 0) + (totals.todays_visitors || 0)} />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="analytics-onpremises-chart">
          <div className="font-display text-base font-semibold text-slate-900 mb-1">On premises right now</div>
          <div className="text-xs text-slate-500 mb-3">Students and visitors currently inside, per location.</div>
          <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 44)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 15, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#334155" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {isSchoolGroup && <Bar dataKey="students" name="Students" fill="#059669" radius={[0, 3, 3, 0]} barSize={12} />}
              <Bar dataKey="visitors" name="Visitors" fill="#2563eb" radius={[0, 3, 3, 0]} barSize={12} />
            </BarChart>
          </ResponsiveContainer>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="analytics-today-chart">
          <div className="font-display text-base font-semibold text-slate-900 mb-1">Activity today</div>
          <div className="text-xs text-slate-500 mb-3">{isSchoolGroup ? "Student events (schools) or visitor check-ins (offices) recorded today." : "Visitor check-ins recorded today."}</div>
          <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 44)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 15, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#334155" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="todays_events" name="Events today" fill="#d97706" radius={[0, 3, 3, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </section>
      </div>
    </main>
  );
}

function TotalCard({ Icon, label, value, accent }) {
  const ring = {
    primary: "ring-primary/20 bg-primary/5",
    emerald: "ring-emerald-200 bg-emerald-50",
  }[accent] || "ring-slate-200 bg-white";
  return (
    <div className={`rounded-2xl border border-slate-200 p-5 ring-1 ${ring}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <Icon className="h-5 w-5 text-slate-400" />
      </div>
      <div className="mt-3 font-display text-4xl font-bold tracking-tight text-slate-900">{value}</div>
    </div>
  );
}
